import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, not } from 'ember-truth-helpers';
import type { BudgetForecastEntry } from 'lumen/models';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './modal-delete-entry.module.css';

interface SettingsBudgetForecastModalsModalDeleteEntrySignature {
  Args: {
    entry: BudgetForecastEntry;
    onClose: (wasDeleted?: boolean) => void;
  };
}

export default class SettingsBudgetForecastModalsModalDeleteEntry extends Component<SettingsBudgetForecastModalsModalDeleteEntrySignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked inputValue = '';

  deleteEntryTask = task(async (): Promise<void> => {
    const { entry, onClose } = this.args;

    try {
      await entry.destroyRecord();

      this.toast.success(
        this.intl.t('budget_forecast.delete_entry.success', {
          title: entry.title,
        }),
        { showIcon: true },
      );

      onClose(true);
    } catch (error) {
      this.toast.error(
        this.intl.t('budget_forecast.delete_entry.fail', {
          title: entry.title,
        }),
        {
          showIcon: true,
        },
      );
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get isInputValueCorrect(): boolean {
    return this.inputValue === this.args.entry.title;
  }

  @action
  onCancelClick(): void {
    this.args.onClose();
  }

  @action
  onDeleteClick(): void {
    if (!this.isInputValueCorrect) {
      return;
    }

    void this.deleteEntryTask.perform();
  }

  @action
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onDeleteClick();
    }
  }

  @action
  setInputValue(event: Event): void {
    this.inputValue = (event.target as HTMLInputElement).value;
  }

  <template>
    <PrismModal @onClose={{@onClose}} class={{styles.confirmation-modal}}>
      <:default>
        <div class="modal--padded flex flex-col items-center justify-center">
          <PrismMessage
            @description={{if
              (eq @entry.dataType "budget")
              (t "budget_forecast.delete_entry.budget.description")
              (t "budget_forecast.delete_entry.forecast.description")
            }}
            @illustration="100x100_warning"
            @title={{t "budget_forecast.delete_entry.title" title=@entry.title}}
            data-test-message
          />

          <div class="flex w-full">
            <PrismInput
              @autofocus={{true}}
              @errorMessage={{if
                (eq @entry.dataType "budget")
                (t "budget_forecast.delete_entry.budget.error.title_mismatch")
                (t "budget_forecast.delete_entry.forecast.error.title_mismatch")
              }}
              @hasErrors={{not this.isInputValueCorrect}}
              @label={{t "general.type_to_confirm" text=@entry.title}}
              @onInput={{this.setInputValue}}
              @onKeyDown={{this.onKeyDown}}
              @value={{this.inputValue}}
              class="w-full"
              data-test-field="confirm-title"
            />
          </div>
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-300 px-500">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.deleteEntryTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{this.onCancelClick}}
            data-test-button="cancel"
          />
          <PrismButton
            @buttonType="critical"
            @isDisabled={{this.deleteEntryTask.isRunning}}
            @isLoading={{this.deleteEntryTask.isRunning}}
            @label={{t "general.delete_forever"}}
            @onClick={{this.onDeleteClick}}
            data-test-button="confirm"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
